import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import {
  MessageSquare,
  Smartphone,
  Globe,
  Send,
  Loader2,
  Check,
  CheckCheck,
  AlertTriangle,
  Ban,
  Plus,
  User,
  Clock,
  UserCheck,
} from 'lucide-react';

// Sigil client conversations: ONE per-contact timeline federating portal
// messages and SMS. Data stays in its home tables server-side; this view
// stitches them and routes replies by medium (Text goes through the gated
// SMS service; Portal creates a portal message + push + email notify).

const fmtPhone = (digits) => {
  const d = (digits || '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  if (ten.length !== 10) return digits || 'unknown';
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = new Date().toDateString() === d.toDateString();
  return sameDay
    ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const ageMinutes = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 60000) : 0);

const AgeBadge = ({ oldestUnreadAt }) => {
  const mins = ageMinutes(oldestUnreadAt);
  if (!oldestUnreadAt || mins < 60) return null;
  const label = mins >= 120 ? `${Math.floor(mins / 60)}h` : `${mins}m`;
  return (
    <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded border border-amber-500/50 text-amber-300 bg-amber-500/10" title={`Oldest unread message has waited ${mins} minutes`}>
      ⏱ {label}
    </span>
  );
};

const StatusTick = ({ status }) => {
  if (status === 'delivered' || status === 'read') {
    return <CheckCheck className="w-3.5 h-3.5 inline text-teal-300" title={status === 'read' ? 'Seen in portal' : 'Delivered'} />;
  }
  if (status === 'delivery_failed') return (
    <span className="text-red-400 inline-flex items-center gap-0.5" title="Not delivered">
      <AlertTriangle className="w-3.5 h-3.5" /> not delivered
    </span>
  );
  if (status === 'queued') return <Clock className="w-3.5 h-3.5 inline text-slate-500" title="Queued" />;
  return <Check className="w-3.5 h-3.5 inline text-slate-400" title="Sent" />;
};

const MediumChip = ({ medium }) => (
  medium === 'sms'
    ? <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide opacity-70"><Smartphone className="w-2.5 h-2.5" />text</span>
    : <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide opacity-70"><Globe className="w-2.5 h-2.5" />portal</span>
);

const TextMessages = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState(searchParams.get('thread') || null);
  const [conv, setConv] = useState(null);
  const [loadingConv, setLoadingConv] = useState(false);
  const [draft, setDraft] = useState('');
  const [medium, setMedium] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [unassigned, setUnassigned] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const scrollRef = useRef(null);

  const loadUnassigned = useCallback(async () => {
    try {
      const r = await api.get('/sigil/unassigned');
      setUnassigned(r.data?.unassigned || []);
    } catch (e) { /* best-effort */ }
  }, []);

  const assign = async (messageId, contactId) => {
    setAssigning(messageId);
    try {
      await api.post(`/sigil/messages/${messageId}/assign`, { contact_id: contactId });
      await Promise.all([loadUnassigned(), loadList()]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Assignment failed.');
    } finally { setAssigning(null); }
  };

  const loadList = useCallback(async () => {
    try {
      const r = await api.get('/sigil/conversations');
      setConvs(r.data?.conversations || []);
    } catch (e) { /* keep prior list */ }
    finally { setLoadingList(false); }
  }, []);

  const defaultMedium = (data) => {
    const msgs = data?.messages || [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].direction === 'inbound') {
        if (msgs[i].medium === 'sms' && data.can_sms) return 'sms';
        if (msgs[i].medium === 'portal' && data.can_portal) return 'portal';
      }
    }
    return data?.can_sms ? 'sms' : (data?.can_portal ? 'portal' : null);
  };

  const loadConv = useCallback(async (key, keepMedium, markRead) => {
    if (!key) { setConv(null); return; }
    setLoadingConv(true);
    try {
      const r = await api.get(`/sigil/conversations/${key}${markRead ? '?mark_read=1' : ''}`);
      setConv(r.data);
      setError(null);
      if (!keepMedium) setMedium(defaultMedium(r.data));
    } catch (e) {
      if (e.response?.status === 404 && /^\d+$/.test(key)) {
        const empty = { key, contact_id: null, contact_name: null, phone_digits: key, suppressed: false, can_sms: true, can_portal: false, messages: [] };
        setConv(empty);
        if (!keepMedium) setMedium('sms');
      } else {
        setError('Could not load the conversation.');
      }
    } finally { setLoadingConv(false); }
  }, []);

  useEffect(() => { loadList(); const t = setInterval(loadList, 20000); return () => clearInterval(t); }, [loadList]);
  useEffect(() => { loadUnassigned(); const t = setInterval(loadUnassigned, 30000); return () => clearInterval(t); }, [loadUnassigned]);
  useEffect(() => { loadConv(selected, false, true); }, [selected, loadConv]);
  useEffect(() => {
    if (!selected) return undefined;
    const t = setInterval(() => loadConv(selected, true, false), 10000);
    return () => clearInterval(t);
  }, [selected, loadConv]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conv?.messages?.length]);

  const pick = (key) => {
    setSelected(key);
    setSearchParams(key ? { thread: key } : {}, { replace: true });
    setDraft('');
    setError(null);
  };

  const startNew = (e) => {
    e.preventDefault();
    const d = newPhone.replace(/\D/g, '');
    const key = d.length === 10 ? `1${d}` : d;
    if (key.length !== 11 || !key.startsWith('1')) {
      setError('Enter a valid US phone number.');
      return;
    }
    setShowNew(false);
    setNewPhone('');
    pick(key);
  };

  const sendReply = async () => {
    const body = draft.trim();
    if (!body || !selected || !medium || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.post(`/sigil/conversations/${selected}/reply`, { body, medium });
      setDraft('');
      await loadConv(selected, true, true);
      loadList();
    } catch (e) {
      setError(e.response?.data?.detail || 'Send failed.');
    } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
  };

  const title = conv?.contact_name || fmtPhone(conv?.phone_digits || selected);
  const openAttachment = async (fileId) => {
    try {
      const r = await api.get(`/files/${fileId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setError('Could not open attachment'); }
  };

  const smsBlocked = conv?.suppressed;
  const canSend = medium === 'portal' ? conv?.can_portal : (conv?.can_sms && !smsBlocked);

  return (
    <div className="h-full flex flex-col p-4 gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-cyan-400">One conversation per client — portal messages and texts together</p>
        <button
          onClick={() => { setShowNew((v) => !v); setError(null); }}
          className="flex items-center gap-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 hover:text-white px-3 py-1.5 rounded-lg text-sm transition"
        >
          <Plus size={16} /><span>New text</span>
        </button>
      </div>

      {showNew && (
        <form onSubmit={startNew} className="flex items-center gap-2">
          <input
            autoFocus
            placeholder="Client phone number"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
            className="px-3 py-2 rounded-lg bg-slate-800 border border-cyan-500/20 text-white text-sm w-64 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          />
          <button type="submit" className="text-sm px-3 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300">Open</button>
        </form>
      )}

      {unassigned.length > 0 && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 space-y-2 max-h-44 overflow-y-auto flex-shrink-0">
          <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold uppercase tracking-wide">
            <UserCheck size={13} /> Unassigned — {unassigned.length} message{unassigned.length === 1 ? '' : 's'} need a person
          </div>
          {unassigned.slice(0, 5).map((m) => (
            <div key={m.id} className="text-sm text-slate-200">
              <span className="text-white font-medium">{fmtPhone(m.from_number)}</span>
              <span className="text-slate-400"> — {(m.body || '').slice(0, 60)}</span>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {m.resolution_status === 'ambiguous' ? (
                  m.candidates.map((c) => (
                    <button
                      key={c.id}
                      disabled={assigning === m.id}
                      onClick={() => assign(m.id, c.id)}
                      className="text-[11px] px-2 py-0.5 rounded border border-amber-500/40 text-amber-200 hover:bg-amber-500/20 disabled:opacity-50"
                    >
                      {assigning === m.id ? '…' : `Assign: ${c.name}`}
                    </button>
                  ))
                ) : (
                  <span className="text-[11px] text-slate-400">unknown number — likely a prospect (intake action coming in Phase B)</span>
                )}
              </div>
            </div>
          ))}
          {unassigned.length > 5 && (
            <div className="text-[11px] text-amber-400/70">+{unassigned.length - 5} more</div>
          )}
        </div>
      )}

      <div className="flex gap-3 flex-1 min-h-0">
        {/* Conversation list */}
        <div className="w-72 flex-shrink-0 overflow-y-auto space-y-1 pr-1">
          {loadingList ? (
            <div className="text-cyan-300 text-sm py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : convs.length === 0 ? (
            <div className="text-slate-400 text-sm py-10 text-center border border-dashed border-slate-600 rounded-xl">
              No client conversations yet.
            </div>
          ) : convs.map((c) => (
            <button
              key={c.key}
              onClick={() => pick(c.key)}
              className={`w-full text-left rounded-lg px-3 py-2.5 border transition-colors ${
                selected === c.key
                  ? 'border-cyan-500/60 bg-slate-700/70'
                  : 'border-slate-600/60 bg-slate-800/50 hover:bg-slate-700/50'
              }`}
            >
              <div className="flex items-center gap-2">
                {c.contact_name
                  ? <User size={13} className="text-cyan-400 flex-shrink-0" />
                  : <MessageSquare size={13} className="text-slate-400 flex-shrink-0" />}
                <span className="text-sm font-medium text-white truncate">
                  {c.contact_name || fmtPhone(c.phone_digits || c.key)}
                </span>
                {c.media?.includes('sms') && <Smartphone size={11} className="text-slate-400 flex-shrink-0" title="Texts" />}
                {c.media?.includes('portal') && <Globe size={11} className="text-slate-400 flex-shrink-0" title="Portal" />}
                {c.suppressed && <Ban size={12} className="text-red-400 flex-shrink-0" title="Texted STOP" />}
                {c.unread > 0 && (
                  <span className="ml-auto flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500 text-white font-semibold">{c.unread}</span>
                )}
                <AgeBadge oldestUnreadAt={c.oldest_unread_at} />
              </div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="text-xs text-slate-300 truncate flex-1">
                  {c.last_direction === 'outbound' ? 'You: ' : ''}{c.last_body}
                </span>
                <span className="text-[10px] text-slate-500 flex-shrink-0">{fmtWhen(c.last_at)}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Timeline pane */}
        <div className="flex-1 flex flex-col rounded-xl border border-slate-600/60 bg-slate-800/40 min-w-0">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              Pick a conversation, or start a new text.
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-600/60 flex items-center gap-3">
                <div>
                  <div className="text-white font-semibold">{title}</div>
                  <div className="text-xs text-cyan-400">
                    {conv?.phone_digits ? fmtPhone(conv.phone_digits) : 'portal only'}
                    {conv?.can_portal ? ' · portal linked' : ''}
                  </div>
                </div>
                {smsBlocked && (
                  <span className="ml-auto text-xs px-2 py-1 rounded border border-red-500/50 text-red-300 bg-red-500/10 flex items-center gap-1">
                    <Ban size={12} /> Texted STOP — SMS off until they text START
                  </span>
                )}
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                {loadingConv && !conv ? (
                  <div className="text-cyan-300 text-sm py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                ) : (conv?.messages || []).length === 0 ? (
                  <div className="text-slate-400 text-sm py-10 text-center">
                    No messages yet. Anything you send goes out on the firm line.
                  </div>
                ) : conv.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${
                      m.direction === 'outbound'
                        ? (m.medium === 'portal' ? 'bg-violet-600/60 text-white rounded-br-sm' : 'bg-cyan-600/70 text-white rounded-br-sm')
                        : (m.medium === 'portal' ? 'bg-slate-700 border border-violet-500/30 text-slate-100 rounded-bl-sm' : 'bg-slate-700 text-slate-100 rounded-bl-sm')
                    }`}>
                      {m.body}
                      {m.attachments?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {m.attachments.map((a, i) => (
                            a.scan_status === 'clean' && a.file_id ? (
                              <button key={i} onClick={() => openAttachment(a.file_id)}
                                className="text-[11px] px-2 py-1 rounded-lg bg-black/20 hover:bg-black/35 border border-white/20 flex items-center gap-1">
                                📎 {(a.content_type || 'file').split('/')[1] || 'file'}
                              </button>
                            ) : (
                              <span key={i} className="text-[11px] px-2 py-1 rounded-lg bg-red-900/40 border border-red-500/40 text-red-200">
                                ⚠️ attachment {a.scan_status === 'infected' ? 'refused (virus scan)' : 'unavailable'}
                              </span>
                            )
                          ))}
                        </div>
                      )}
                      <div className={`text-[10px] mt-1 flex items-center gap-1.5 ${m.direction === 'outbound' ? 'text-white/70 justify-end' : 'text-slate-400'}`}>
                        <MediumChip medium={m.medium} />
                        {m.direction === 'outbound' && m.sender_name ? <span>{m.sender_name}</span> : null}
                        <span>{fmtWhen(m.created_at)}</span>
                        {m.direction === 'outbound' && <StatusTick status={m.status} />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-4 py-3 border-t border-slate-600/60">
                {conv?.shared_identities?.length > 1 && (
                  <div className="mb-2 text-xs px-3 py-2 rounded-lg border border-amber-500/50 bg-amber-500/10 text-amber-200 flex items-start gap-2">
                    <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>
                      This number is shared between <b>{conv.shared_identities.join(' and ')}</b>.
                      Anyone with the handset can read this message.
                    </span>
                  </div>
                )}
                {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
                <div className="flex items-center gap-1 mb-2">
                  <button
                    onClick={() => setMedium('sms')}
                    disabled={!conv?.can_sms || smsBlocked}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium flex items-center gap-1 transition ${
                      medium === 'sms' ? 'bg-cyan-500/30 text-white' : 'text-slate-400 hover:text-cyan-300'
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={smsBlocked ? 'This number texted STOP' : (!conv?.can_sms ? 'No phone number on file' : 'Send as text message')}
                  >
                    <Smartphone size={11} /> Text
                  </button>
                  <button
                    onClick={() => setMedium('portal')}
                    disabled={!conv?.can_portal}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-medium flex items-center gap-1 transition ${
                      medium === 'portal' ? 'bg-violet-500/30 text-white' : 'text-slate-400 hover:text-violet-300'
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={conv?.can_portal ? 'Send as portal message (client is notified)' : 'No portal contact linked'}
                  >
                    <Globe size={11} /> Portal
                  </button>
                </div>
                {!canSend ? (
                  <div className="text-xs text-slate-400 py-1">
                    {smsBlocked && medium !== 'portal'
                      ? 'This number opted out of texting. They can opt back in by texting START.'
                      : 'No send channel available for this conversation.'}
                  </div>
                ) : (
                  <div className="flex items-end gap-2">
                    <textarea
                      rows={2}
                      placeholder={medium === 'portal' ? 'Portal message… (Enter to send)' : 'Text message… (Enter to send)'}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={onKey}
                      className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-cyan-500/20 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                    />
                    <button
                      onClick={sendReply}
                      disabled={sending || !draft.trim()}
                      className={`px-4 py-2.5 rounded-lg text-white disabled:opacity-50 flex items-center gap-2 transition-all ${
                        medium === 'portal'
                          ? 'bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600'
                          : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600'
                      }`}
                    >
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                      Send
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextMessages;
